import { getSelectedCardIds } from '../components/BeerCard/BeerCardContainer';
import { Beer } from '../state/types';
import { UserInteractionsState } from '../state/types';

export interface RouletteSegment {
  index: number;
  beers: Beer[];
}

const TOTAL_SEGMENTS = 36;

export function getBrewletteBeers(
  allBeers: Beer[],
  activeTab: string,
  userInteractions: Pick<UserInteractionsState, 'tastingList' | 'triedList' | 'favorites' | 'ratings' | 'notes'>,
  showOnlyFavorites = false,
  selectedIdsOverride?: Set<string>
): Beer[] {
  if (activeTab === '1') {
    const tastingSet = new Set(userInteractions.tastingList);

    if (showOnlyFavorites) {
      return allBeers.filter((beer) => tastingSet.has(beer.id));
    }

    const triedSet = new Set(userInteractions.triedList ?? []);

    return allBeers.filter((beer) => {
      if (tastingSet.has(beer.id)) {
        return false;
      }

      const isInTriedList = triedSet.has(beer.id);
      const isFavorited = userInteractions.favorites.includes(beer.id);
      const hasRating = (userInteractions.ratings[beer.id] ?? 0) > 0;
      const notes = userInteractions.notes[beer.id];
      const hasNotes = typeof notes === 'string' && notes.trim() !== '';

      return isInTriedList || isFavorited || hasRating || hasNotes;
    });
  }

  const selectedIds = selectedIdsOverride ?? getSelectedCardIds();
  if (selectedIds.size === 0) {
    return [];
  }

  return allBeers.filter((beer) => selectedIds.has(beer.id));
}

export function createRouletteSegments(beers: Beer[], totalSegments = TOTAL_SEGMENTS): RouletteSegment[] {
  const segments: RouletteSegment[] = Array.from({ length: totalSegments }, (_, index) => ({
    index,
    beers: []
  }));

  beers.forEach((beer, beerIndex) => {
    const targetSegment = beerIndex % totalSegments;
    segments[targetSegment].beers.push(beer);
  });

  return segments;
}

export function pickSpinTarget(
  segments: RouletteSegment[],
  rng: () => number = Math.random
): { segmentIndex: number; beer: Beer } {
  const nonEmptySegments = segments.filter((segment) => segment.beers.length > 0);
  if (nonEmptySegments.length === 0) {
    throw new Error('Cannot spin Brewlette with no beers');
  }

  const segment = nonEmptySegments[Math.floor(rng() * nonEmptySegments.length)];
  const beer = segment.beers[Math.floor(rng() * segment.beers.length)];

  return {
    segmentIndex: segment.index,
    beer
  };
}

export function getSpinDegrees(segmentIndex: number, fullSpins = 6, totalSegments = TOTAL_SEGMENTS): number {
  const segmentAngle = 360 / totalSegments;
  const pointerOffset = segmentAngle / 2;
  const landingAngle = 360 - segmentIndex * segmentAngle - pointerOffset;
  return fullSpins * 360 + landingAngle;
}
